import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Bot, FileDown, ListFilter } from "lucide-react";
import { AdsTab } from "./AdsTab";
import { CompanyTab } from "./CompanyTab";
import { FinanceConnectionControls } from "./FinanceConnectionControls";
import { FinanceTabs } from "./FinanceTabs";
import type { FinanceTabId } from "./FinanceTabs";
import { FunnelsTab } from "./FunnelsTab";
import { LogisticsTab } from "./LogisticsTab";
import { ReportsTab } from "./ReportsTab";
import type { DateRange, FunnelSortKey, FunnelSyncFilter } from "./funnels/types";
import { PeriodSelector } from "../funnel-detail/PeriodSelector";
import type { Period } from "../funnel-detail/types";
import { createDefaultPeriod, revivePeriod } from "../funnel-detail/periods";

const financeTabs: Array<{ id: FinanceTabId; label: string }> = [
  { id: "company", label: "Tu empresa" },
  { id: "logistics", label: "Logística" },
  { id: "ads", label: "Marketing" },
  { id: "funnels", label: "Embudos" },
  { id: "reports", label: "Reportes" },
];

const PERIOD_STORAGE_KEY = "finance_period";

const readStoredPeriod = (): Period => {
  if (typeof window === "undefined") return createDefaultPeriod();
  const stored = sessionStorage.getItem(PERIOD_STORAGE_KEY);
  if (!stored) return createDefaultPeriod();
  try {
    return revivePeriod(JSON.parse(stored)) ?? createDefaultPeriod();
  } catch {
    return createDefaultPeriod();
  }
};

interface DashboardWithTabsProps {
  activeTab: FinanceTabId;
  onTabChange: (tab: FinanceTabId) => void;
}

export const DashboardWithTabs = ({ activeTab, onTabChange }: DashboardWithTabsProps) => {
  const [period, setPeriod] = useState<Period>(() => readStoredPeriod());
  const [companyExportHandler, setCompanyExportHandler] = useState<(() => void) | null>(null);
  const [funnelsExportHandler, setFunnelsExportHandler] = useState<(() => void) | null>(null);
  const [funnelSortBy, setFunnelSortBy] = useState<FunnelSortKey>("profit");
  const [funnelSyncFilter, setFunnelSyncFilter] = useState<FunnelSyncFilter>("all");

  const dateRange = useMemo<DateRange>(
    () => ({
      initialDate: period.from,
      finalDate: period.to,
      label: period.label,
    }),
    [period]
  );

  const handleChange = (tab: FinanceTabId) => {
    sessionStorage.setItem("finance_active_tab", tab);
    onTabChange(tab);
  };

  const handlePeriodChange = (nextPeriod: Period) => {
    sessionStorage.setItem(PERIOD_STORAGE_KEY, JSON.stringify(nextPeriod));
    setPeriod(nextPeriod);
  };

  const registerCompanyExport = useCallback((handler: (() => void) | null) => {
    setCompanyExportHandler(() => handler);
  }, []);

  const registerFunnelsExport = useCallback((handler: (() => void) | null) => {
    setFunnelsExportHandler(() => handler);
  }, []);

  const handleOpenAi = () => {
    window.dispatchEvent(new Event("finance:open-ai-chat"));
  };

  return (
    <>
      <h1 id="finance-title-target" className="text-2xl font-bold tracking-tight mb-6">Fluxi Finance</h1>
      <div className={`mb-8 w-full ${activeTab !== "reports" ? "pr-12" : ""}`}>
        <div className="md:hidden">
          <div className="mt-[30px]"></div>
        </div>
        <div className="flex w-full flex-wrap items-center justify-between gap-4">
          <div id="finance-tabs-target">
            <FinanceTabs tabs={financeTabs} activeTab={activeTab} onChange={handleChange} />
          </div>
          {activeTab !== "reports" ? (
            <div className="flex flex-wrap items-center gap-2 md:ml-auto md:justify-end">
              <button
                onClick={handleOpenAi}
                className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-blue-100 bg-white text-[#2b5ce6] shadow-sm transition hover:bg-blue-50"
                type="button"
                title="Pregúntale a la IA"
                aria-label="Pregúntale a la IA"
              >
                <Bot className="h-5 w-5" />
              </button>
              {activeTab === "company" ? (
                <button
                  onClick={() => companyExportHandler?.()}
                  disabled={!companyExportHandler}
                  className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-500 shadow-sm transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                  type="button"
                  title="Exportar PDF"
                  aria-label="Exportar PDF"
                >
                  <FileDown className="h-4 w-4 text-[#2b5ce6]" />
                </button>
              ) : null}
              {activeTab === "funnels" ? (
                <button
                  onClick={() => funnelsExportHandler?.()}
                  disabled={!funnelsExportHandler}
                  className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-500 shadow-sm transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                  type="button"
                  title="Estado de embudos"
                  aria-label="Estado de embudos"
                >
                  <FileDown className="h-4 w-4 text-[#2b5ce6]" />
                </button>
              ) : null}
              {activeTab === "funnels" ? (
                <FunnelHeaderFilters
                  sortBy={funnelSortBy}
                  syncFilter={funnelSyncFilter}
                  onSortChange={setFunnelSortBy}
                  onSyncFilterChange={setFunnelSyncFilter}
                />
              ) : (
                <div id="finance-date-controls-target">
                  <PeriodSelector value={period} onChange={handlePeriodChange} />
                </div>
              )}
              <FinanceConnectionControls period={period} />
            </div>
          ) : null}
        </div>
      </div>

      <div id={`finance-${activeTab}-surface-target`}>
        {activeTab === "funnels" ? (
          <FunnelsTab
            dateRange={dateRange}
            period={period}
            sortBy={funnelSortBy}
            syncFilter={funnelSyncFilter}
            onRegisterExport={registerFunnelsExport}
          />
        ) : null}
        {activeTab === "ads" ? <AdsTab period={period} /> : null}
        {activeTab === "company" ? <CompanyTab period={period} onRegisterExport={registerCompanyExport} /> : null}
        {activeTab === "logistics" ? <LogisticsTab period={period} /> : null}
        {activeTab === "reports" ? <ReportsTab period={period} /> : null}
      </div>
    </>
  );
};

function FunnelHeaderFilters({
  sortBy,
  syncFilter,
  onSortChange,
  onSyncFilterChange,
}: {
  sortBy: FunnelSortKey;
  syncFilter: FunnelSyncFilter;
  onSortChange: (value: FunnelSortKey) => void;
  onSyncFilterChange: (value: FunnelSyncFilter) => void;
}) {
  const [open, setOpen] = useState(false);
  const popoverRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="relative">
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setOpen((value) => !value)}
        className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-500 shadow-sm transition hover:bg-gray-50"
        title="Filtrar embudos"
        aria-label="Filtrar embudos"
      >
        <ListFilter className="h-4 w-4 text-[#2b5ce6]" />
      </button>
      <div
        ref={popoverRef}
        className={`absolute right-0 top-12 z-30 w-72 origin-top-right rounded-2xl border border-gray-200 bg-white p-3 shadow-xl transition-all duration-200 ${
          open ? "scale-100 opacity-100" : "pointer-events-none scale-95 opacity-0"
        }`}
      >
        <div className="space-y-3">
          <label className="block">
            <span className="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-gray-400">
              Ordenar
            </span>
            <select
              value={sortBy}
              onChange={(event) => onSortChange(event.target.value as FunnelSortKey)}
              aria-label="Ordenar embudos"
              className="h-10 w-full rounded-full border border-gray-200 bg-white px-4 text-sm font-semibold text-gray-700 outline-none transition hover:bg-gray-50 focus:border-[#2b5ce6]"
            >
              <option value="profit">Mayor utilidad</option>
              <option value="sales">Mayores ventas</option>
              <option value="cpa">Mayor CPA</option>
            </select>
          </label>
          <label className="block">
            <span className="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-gray-400">
              Sincronización
            </span>
            <select
              value={syncFilter}
              onChange={(event) => onSyncFilterChange(event.target.value as FunnelSyncFilter)}
              aria-label="Filtrar sincronización"
              className="h-10 w-full rounded-full border border-gray-200 bg-white px-4 text-sm font-semibold text-gray-700 outline-none transition hover:bg-gray-50 focus:border-[#2b5ce6]"
            >
              <option value="all">Todos</option>
              <option value="synced">Sincronizados</option>
              <option value="unsynced">No sincronizados</option>
            </select>
          </label>
        </div>
      </div>
    </div>
  );
}
